import React, { useState, useRef, useEffect } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ProfessionalEmailBuilder.css';

const ProfessionalEmailBuilder = ({ onSave, initialTemplate = null }) => {
    const [emailContent, setEmailContent] = useState({
        backgroundColor: '#f3f4f6',
        containerWidth: '600',
        // Email Blocks
        blocks: initialTemplate?.blocks || [
            {
                id: 'header-1',
                type: 'header',
                style: {
                    backgroundColor: '#ffffff',
                    padding: '40px 20px'
                },
                content: {
                    logo: '',
                    title: 'Email Title',
                    titleColor: '#1e293b',
                    titleSize: '28px',
                    titleFontFamily: 'Jost, Arial, sans-serif',
                    subtitle: 'Email subtitle or description',
                    subtitleColor: '#64748b',
                    subtitleSize: '16px',
                    subtitleFontFamily: 'Arial, sans-serif'
                },
                alignment: 'center'
            },
            {
                id: 'text-1',
                type: 'text',
                style: {
                    backgroundColor: '#ffffff',
                    padding: '20px'
                },
                content: {
                    text: 'Hello [customer.name],\n\nWe are excited to have you on board! Your journey with us starts now.',
                    color: '#374151',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    textAlign: 'left',
                    fontFamily: 'Arial, sans-serif'
                },
                alignment: 'left'
            },
            {
                id: 'button-1',
                type: 'button',
                style: {
                    backgroundColor: '#ffffff',
                    padding: '30px 20px'
                },
                content: {
                    text: 'Get Started',
                    url: 'https://bonusclub.com',
                    backgroundColor: '#667eea',
                    textColor: '#ffffff',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                },
                alignment: 'center'
            },
            {
                id: 'footer-1',
                type: 'footer',
                style: {
                    backgroundColor: '#f8fafc',
                    padding: '30px 20px',
                    textAlign: 'center'
                },
                content: {
                    companyName: 'BonusClub',
                    address: 'Your Company Address\nCity, Country',
                    unsubscribeText: 'Unsubscribe from these emails',
                    unsubscribeUrl: '[unsubscribe_link]',
                    textColor: '#6b7280',
                    fontSize: '14px',
                    fontFamily: 'Arial, sans-serif'
                }
            }
        ]
    });

    const [selectedBlock, setSelectedBlock] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [uploading, setUploading] = useState(false);
    const previewRef = useRef(null);
    const fileInputRef = useRef(null);

    // Available fonts for email
    const emailFonts = [
        { name: 'Arial', value: 'Arial, sans-serif', category: 'Sans Serif' },
        { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif', category: 'Sans Serif' },
        { name: 'Verdana', value: 'Verdana, Geneva, sans-serif', category: 'Sans Serif' },
        { name: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif', category: 'Sans Serif' },
        { name: 'Jost', value: 'Jost, Arial, sans-serif', category: 'Custom' },
        { name: 'Times New Roman', value: 'Times New Roman, Times, serif', category: 'Serif' },
        { name: 'Georgia', value: 'Georgia, Times, serif', category: 'Serif' },
        { name: 'Courier New', value: 'Courier New, Courier, monospace', category: 'Monospace' },
        { name: 'Tahoma', value: 'Tahoma, Geneva, sans-serif', category: 'Sans Serif' },
        { name: 'Lucida Grande', value: 'Lucida Grande, Lucida Sans Unicode, sans-serif', category: 'Sans Serif' }
    ];

    const addBlock = (type) => {
        const newBlock = {
            id: `${type}-${Date.now()}`,
            type,
            style: getDefaultBlockStyle(type),
            content: getDefaultBlockContent(type)
        };

        setEmailContent(prev => ({
            ...prev,
            blocks: [...prev.blocks, newBlock]
        }));
    };

    const getDefaultBlockStyle = (type) => {
        switch (type) {
            case 'text':
                return {
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    textAlign: 'left'
                };
            case 'button':
                return {
                    backgroundColor: '#ffffff',
                    padding: '30px 20px',
                    textAlign: 'center'
                };
            case 'image':
                return {
                    backgroundColor: '#ffffff',
                    padding: '20px',
                    textAlign: 'center'
                };
            case 'spacer':
                return {
                    backgroundColor: '#ffffff',
                    height: '30px'
                };
            default:
                return {
                    backgroundColor: '#ffffff',
                    padding: '20px'
                };
        }
    };

    const getDefaultBlockContent = (type) => {
        switch (type) {
            case 'text':
                return {
                    text: 'Your email content goes here...',
                    color: '#374151',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    fontFamily: 'Arial, sans-serif',
                    textAlign: 'left',
                    alignment: 'left'
                };
            case 'button':
                return {
                    text: 'Click Here',
                    url: 'https://example.com',
                    backgroundColor: '#667eea',
                    textColor: '#ffffff',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    fontFamily: 'Arial, sans-serif',
                    alignment: 'center'
                };
            case 'image':
                return {
                    src: '',
                    alt: 'Image description',
                    width: '300px',
                    alignment: 'center'
                };
            case 'spacer':
                return {};
            default:
                return {};
        }
    };

    const updateBlock = (blockId, updates) => {
        setEmailContent(prev => ({
            ...prev,
            blocks: prev.blocks.map(block =>
                block.id === blockId
                    ? { 
                        ...block, 
                        content: { ...block.content, ...(updates.content || {}) },
                        style: { ...block.style, ...(updates.style || {}) }
                    }
                    : block
            )
        }));
        
        // Update selected block if it's the one being updated
        if (selectedBlock?.id === blockId) {
            setSelectedBlock(prev => prev ? {
                ...prev,
                content: { ...prev.content, ...(updates.content || {}) },
                style: { ...prev.style, ...(updates.style || {}) }
            } : prev);
        }
    };

    // Helper function for content updates
    const updateBlockContent = (blockId, contentUpdates) => {
        console.log('Updating block content:', blockId, contentUpdates);
        updateBlock(blockId, { content: contentUpdates });
    };

    // Helper function for style updates  
    const updateBlockStyle = (blockId, styleUpdates) => {
        updateBlock(blockId, { style: styleUpdates });
    };

    const deleteBlock = (blockId) => {
        setEmailContent(prev => ({
            ...prev,
            blocks: prev.blocks.filter(block => block.id !== blockId)
        }));
    };

    const moveBlockUp = (blockId) => {
        setEmailContent(prev => {
            const blocks = [...prev.blocks];
            const index = blocks.findIndex(b => b.id === blockId);
            if (index > 0) {
                [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
            }
            return { ...prev, blocks };
        });
    };

    const moveBlockDown = (blockId) => {
        setEmailContent(prev => {
            const blocks = [...prev.blocks];
            const index = blocks.findIndex(b => b.id === blockId);
            if (index < blocks.length - 1) {
                [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
            }
            return { ...prev, blocks };
        });
    };

    const generateEmailHtml = () => {
        const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${emailContent.subject}</title>
    <style>
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        .email-container { max-width: ${emailContent.containerWidth}px; margin: 0 auto; background-color: ${emailContent.backgroundColor}; }
        .email-block { display: block; width: 100%; }
        .button-link { display: inline-block; text-decoration: none; }
        img { max-width: 100%; height: auto; }
        @media only screen and (max-width: 600px) {
            .email-container { width: 100% !important; }
            .email-block { width: 100% !important; }
        }
    </style>
</head>
<body>
    <div class="email-container">
        ${emailContent.blocks.map(block => renderBlockToHtml(block)).join('')}
    </div>
</body>
</html>`;
        return html;
    };

    const renderBlockToHtml = (block) => {
        const style = Object.entries(block.style)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');

        switch (block.type) {
            case 'header':
                return `
                    <div class="email-block" style="${style}; text-align: ${block.alignment || 'center'};">
                        ${block.content.logo ? `<img src="${block.content.logo}" alt="Logo" style="width: 150px; margin-bottom: 20px;">` : ''}
                        <h1 style="margin: 0 0 10px 0; font-size: ${block.content.titleSize}; color: ${block.content.titleColor}; font-weight: bold; font-family: ${block.content.titleFontFamily || 'Arial, sans-serif'};">
                            ${block.content.title}
                        </h1>
                        ${block.content.subtitle ? `<p style="margin: 0; font-size: ${block.content.subtitleSize}; color: ${block.content.subtitleColor}; font-family: ${block.content.subtitleFontFamily || 'Arial, sans-serif'};">${block.content.subtitle}</p>` : ''}
                    </div>
                `;

            case 'text':
                return `
                    <div class="email-block" style="${style}; text-align: ${block.alignment || 'left'};">
                        <p style="margin: 0; font-size: ${block.content.fontSize}; color: ${block.content.color}; line-height: ${block.content.lineHeight}; text-align: ${block.content.textAlign}; font-family: ${block.content.fontFamily || 'Arial, sans-serif'};">
                            ${block.content.text.replace(/\n/g, '<br>')}
                        </p>
                    </div>
                `;

            case 'button':
                return `
                    <div class="email-block" style="${style}; text-align: ${block.alignment || 'center'};">
                        <a href="${block.content.url}" class="button-link" style="background-color: ${block.content.backgroundColor}; color: ${block.content.textColor}; padding: ${block.content.padding}; border-radius: ${block.content.borderRadius}; font-size: ${block.content.fontSize}; font-weight: ${block.content.fontWeight}; font-family: ${block.content.fontFamily || 'Arial, sans-serif'}; text-decoration: none; display: inline-block;">
                            ${block.content.text}
                        </a>
                    </div>
                `;

            case 'image':
                return block.content.src ? `
                    <div class="email-block" style="${style}; text-align: ${block.alignment || 'center'};">
                        <img src="${block.content.src}" alt="${block.content.alt}" style="width: ${block.content.width}; max-width: 100%; height: auto; border-radius: 4px;">
                    </div>
                ` : '';

            case 'spacer':
                return `<div class="email-block" style="${style}"></div>`;

            case 'footer':
                return `
                    <div class="email-block" style="${style}">
                        <p style="margin: 0 0 10px 0; font-size: ${block.content.fontSize}; color: ${block.content.textColor}; font-weight: bold; font-family: ${block.content.fontFamily || 'Arial, sans-serif'};">
                            ${block.content.companyName}
                        </p>
                        <p style="margin: 0 0 15px 0; font-size: ${block.content.fontSize}; color: ${block.content.textColor}; line-height: 1.4; font-family: ${block.content.fontFamily || 'Arial, sans-serif'};">
                            ${block.content.address.replace(/\n/g, '<br>')}
                        </p>
                        <a href="${block.content.unsubscribeUrl}" style="font-size: 12px; color: ${block.content.textColor}; text-decoration: underline;">
                            ${block.content.unsubscribeText}
                        </a>
                    </div>
                `;

            default:
                return '';
        }
    };

    const handleSave = () => {
        const html = generateEmailHtml();
        if (onSave) {
            onSave({
                html: html,
                design: emailContent,
                mergeTags: ['[customer.name]', '[customer.email]', '[product.title]', '[unsubscribe_link]']
            });
        }
    };

    const uploadImage = async (file, blockId) => {
        if (!file) return;

        setUploading(true);
        try {
            const storage = getStorage();
            const imageRef = storageRef(storage, `email-images/${Date.now()}-${file.name}`);
            
            // Upload file
            const snapshot = await uploadBytes(imageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            // Update block with new image URL
            updateBlockContent(blockId, { src: downloadURL });
            
            console.log('Image uploaded successfully:', downloadURL);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const openPreview = () => {
        const html = generateEmailHtml();
        const previewWindow = window.open('', '_blank');
        previewWindow.document.write(html);
        previewWindow.document.close();
    };

    return (
        <div className="professional-email-builder">
            {/* Toolbar */}
            <div className="email-toolbar">
                <div className="toolbar-section">
                    <h4>📧 Email Builder</h4>
                </div>
                
                <div className="toolbar-section">
                    <p className="toolbar-info">Design your email template - subject will be set in Email Flow</p>
                </div>

                <div className="toolbar-actions">
                    <button className="toolbar-btn secondary" onClick={openPreview}>
                        👁️ Preview
                    </button>
                    <button className="toolbar-btn primary" onClick={handleSave}>
                        💾 Save Template
                    </button>
                </div>
            </div>

            <div className="email-builder-main">
                {/* Sidebar */}
                <div className="email-builder-sidebar">
                    <div className="sidebar-section">
                        <div className="info-card">
                            <h6>📧 Template Builder</h6>
                            <p>Create reusable email blocks. Click blocks to edit, drag controls to reorder.</p>
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <h5>➕ Add Blocks</h5>
                        <div className="block-buttons">
                            <button className="block-btn text" onClick={() => addBlock('text')}>
                                <span className="block-icon">📝</span>
                                <span className="block-label">Text</span>
                            </button>
                            <button className="block-btn button" onClick={() => addBlock('button')}>
                                <span className="block-icon">🔘</span>
                                <span className="block-label">Button</span>
                            </button>
                            <button className="block-btn image" onClick={() => addBlock('image')}>
                                <span className="block-icon">🖼️</span>
                                <span className="block-label">Image</span>
                            </button>
                            <button className="block-btn spacer" onClick={() => addBlock('spacer')}>
                                <span className="block-icon">📏</span>
                                <span className="block-label">Spacer</span>
                            </button>
                        </div>
                    </div>

                    {selectedBlock && (
                        <div className="sidebar-section">
                            <h5>⚙️ Block Settings</h5>
                            {renderBlockEditor(selectedBlock)}
                        </div>
                    )}

                    <div className="sidebar-section">
                        <h5>🎨 Email Settings</h5>
                        <div className="email-settings">
                            <label>Background Color</label>
                            <input
                                type="color"
                                value={emailContent.backgroundColor}
                                onChange={(e) => setEmailContent(prev => ({ ...prev, backgroundColor: e.target.value }))}
                            />
                            
                            <label>Container Width</label>
                            <input
                                type="number"
                                value={emailContent.containerWidth}
                                onChange={(e) => setEmailContent(prev => ({ ...prev, containerWidth: e.target.value }))}
                                min="300"
                                max="800"
                            />
                        </div>
                    </div>
                </div>

                {/* Email Canvas */}
                <div className="email-canvas">
                    <div className="canvas-header">
                        <h4>📱 Live Preview</h4>
                        <div className="canvas-info">
                            <span className="preview-badge">Real-time</span>
                        </div>
                    </div>

                    <div className="canvas-content">
                        <div 
                            className="email-preview"
                            style={{ 
                                backgroundColor: emailContent.backgroundColor,
                                minHeight: '400px',
                                padding: '20px'
                            }}
                        >
                            <div 
                                className="email-container-preview"
                                style={{ 
                                    maxWidth: `${emailContent.containerWidth}px`,
                                    margin: '0 auto',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                                }}
                            >
                                {emailContent.blocks.map((block, index) => (
                                    <div
                                        key={block.id}
                                        className={`email-block-preview ${selectedBlock?.id === block.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedBlock(block)}
                                        style={block.style}
                                    >
                                        <div className="block-controls">
                                            <button 
                                                className="control-btn move-up"
                                                onClick={(e) => { e.stopPropagation(); moveBlockUp(block.id); }}
                                                title="Move Up"
                                            >
                                                ↑
                                            </button>
                                            <button 
                                                className="control-btn move-down"
                                                onClick={(e) => { e.stopPropagation(); moveBlockDown(block.id); }}
                                                title="Move Down"
                                            >
                                                ↓
                                            </button>
                                            <button 
                                                className="control-btn delete"
                                                onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
                                                title="Delete Block"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                        {renderBlockPreview(block)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    function renderBlockPreview(block) {
        switch (block.type) {
            case 'header':
                return (
                    <div style={{ textAlign: block.alignment || 'center' }}>
                        {block.content.logo && (
                            <img 
                                src={block.content.logo} 
                                alt="Logo" 
                                style={{ width: '150px', marginBottom: '20px' }}
                            />
                        )}
                        <h1 style={{
                            margin: '0 0 10px 0',
                            fontSize: block.content.titleSize,
                            color: block.content.titleColor,
                            fontWeight: 'bold',
                            fontFamily: block.content.titleFontFamily || 'Arial, sans-serif'
                        }}>
                            {block.content.title}
                        </h1>
                        {block.content.subtitle && (
                            <p style={{
                                margin: '0',
                                fontSize: block.content.subtitleSize,
                                color: block.content.subtitleColor,
                                fontFamily: block.content.subtitleFontFamily || 'Arial, sans-serif'
                            }}>
                                {block.content.subtitle}
                            </p>
                        )}
                    </div>
                );

            case 'text':
                return (
                    <div style={{ textAlign: block.alignment || 'left' }}>
                        <p style={{
                            margin: '0',
                            fontSize: block.content.fontSize,
                            color: block.content.color,
                            lineHeight: block.content.lineHeight,
                            textAlign: block.content.textAlign,
                            fontFamily: block.content.fontFamily || 'Arial, sans-serif'
                        }}>
                            {block.content.text.split('\n').map((line, i) => (
                                <span key={i}>
                                    {line}
                                    {i < block.content.text.split('\n').length - 1 && <br />}
                                </span>
                            ))}
                        </p>
                    </div>
                );

            case 'button':
                return (
                    <div style={{ textAlign: block.alignment || 'center' }}>
                        <a
                            href={block.content.url}
                            style={{
                                backgroundColor: block.content.backgroundColor,
                                color: block.content.textColor,
                                padding: block.content.padding,
                                borderRadius: block.content.borderRadius,
                                fontSize: block.content.fontSize,
                                fontWeight: block.content.fontWeight,
                                fontFamily: block.content.fontFamily || 'Arial, sans-serif',
                                textDecoration: 'none',
                                display: 'inline-block'
                            }}
                        >
                            {block.content.text}
                        </a>
                    </div>
                );

            case 'image':
                return block.content.src ? (
                    <div style={{ textAlign: block.alignment || 'center' }}>
                        <img 
                            src={block.content.src} 
                            alt={block.content.alt}
                            style={{ 
                                width: block.content.width,
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '4px'
                            }}
                            onLoad={() => console.log('Image loaded:', block.content.src)}
                            onError={() => console.error('Image failed to load:', block.content.src)}
                        />
                    </div>
                ) : (
                    <div style={{ 
                        padding: '40px',
                        border: '2px dashed #d1d5db',
                        textAlign: 'center',
                        color: '#9ca3af',
                        borderRadius: '8px'
                    }}>
                        🖼️ Upload image or add URL in settings
                    </div>
                );

            case 'spacer':
                return <div style={{ height: block.style.height || '30px' }}></div>;

            case 'footer':
                return (
                    <div style={{ textAlign: block.style.textAlign }}>
                        <p style={{
                            margin: '0 0 10px 0',
                            fontSize: block.content.fontSize,
                            color: block.content.textColor,
                            fontWeight: 'bold',
                            fontFamily: block.content.fontFamily || 'Arial, sans-serif'
                        }}>
                            {block.content.companyName}
                        </p>
                        <p style={{
                            margin: '0 0 15px 0',
                            fontSize: block.content.fontSize,
                            color: block.content.textColor,
                            lineHeight: '1.4',
                            fontFamily: block.content.fontFamily || 'Arial, sans-serif'
                        }}>
                            {block.content.address.split('\n').map((line, i) => (
                                <span key={i}>
                                    {line}
                                    {i < block.content.address.split('\n').length - 1 && <br />}
                                </span>
                            ))}
                        </p>
                        <a 
                            href={block.content.unsubscribeUrl}
                            style={{
                                fontSize: '12px',
                                color: block.content.textColor,
                                textDecoration: 'underline'
                            }}
                        >
                            {block.content.unsubscribeText}
                        </a>
                    </div>
                );

            default:
                return <div>Unknown block type</div>;
        }
    }

    function renderBlockEditor(block) {
        switch (block.type) {
            case 'header':
                return (
                    <div className="block-editor">
                        <label>Title</label>
                        <input
                            type="text"
                            value={block.content.title}
                            onChange={(e) => updateBlockContent(block.id, { title: e.target.value })}
                        />
                        
                        <label>Subtitle</label>
                        <input
                            type="text"
                            value={block.content.subtitle}
                            onChange={(e) => updateBlockContent(block.id, { subtitle: e.target.value })}
                        />
                        
                        <label>Logo URL</label>
                        <input
                            type="url"
                            value={block.content.logo}
                            onChange={(e) => updateBlockContent(block.id, { logo: e.target.value })}
                        />

                        <label>Title Color</label>
                        <input
                            type="color"
                            value={block.content.titleColor}
                            onChange={(e) => updateBlockContent(block.id, { titleColor: e.target.value })}
                        />

                        <label>Title Font</label>
                        <select
                            value={block.content.titleFontFamily}
                            onChange={(e) => updateBlockContent(block.id, { titleFontFamily: e.target.value })}
                        >
                            {emailFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                    {font.name} ({font.category})
                                </option>
                            ))}
                        </select>

                        <label>Subtitle Font</label>
                        <select
                            value={block.content.subtitleFontFamily}
                            onChange={(e) => updateBlockContent(block.id, { subtitleFontFamily: e.target.value })}
                        >
                            {emailFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                    {font.name} ({font.category})
                                </option>
                            ))}
                        </select>

                        <label>Header Alignment</label>
                        <div className="alignment-toggle">
                            <button 
                                className={`align-btn ${block.alignment === 'left' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'left' })}
                            >
                                ⬅️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'center' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'center' })}
                            >
                                ↔️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'right' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'right' })}
                            >
                                ➡️
                            </button>
                        </div>

                        <label>Background Color</label>
                        <input
                            type="color"
                            value={block.style.backgroundColor}
                            onChange={(e) => updateBlockStyle(block.id, { backgroundColor: e.target.value })}
                        />
                    </div>
                );

            case 'text':
                return (
                    <div className="block-editor">
                        <label>Text Content</label>
                        <textarea
                            value={block.content.text}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, text: e.target.value }
                            })}
                            rows={4}
                        />
                        <small>💡 Use [customer.name], [product.title] for personalization</small>

                        <label>Text Color</label>
                        <input
                            type="color"
                            value={block.content.color}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, color: e.target.value }
                            })}
                        />

                        <label>Font Size</label>
                        <select
                            value={block.content.fontSize}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, fontSize: e.target.value }
                            })}
                        >
                            <option value="12px">Small (12px)</option>
                            <option value="14px">Normal (14px)</option>
                            <option value="16px">Medium (16px)</option>
                            <option value="18px">Large (18px)</option>
                            <option value="20px">Extra Large (20px)</option>
                        </select>

                        <label>Font Family</label>
                        <select
                            value={block.content.fontFamily}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, fontFamily: e.target.value }
                            })}
                        >
                            {emailFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                    {font.name} ({font.category})
                                </option>
                            ))}
                        </select>

                        <label>Block Alignment</label>
                        <div className="alignment-toggle">
                            <button 
                                className={`align-btn ${block.alignment === 'left' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'left' })}
                            >
                                ⬅️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'center' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'center' })}
                            >
                                ↔️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'right' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'right' })}
                            >
                                ➡️
                            </button>
                        </div>

                        <label>Text Align</label>
                        <div className="alignment-toggle">
                            <button 
                                className={`align-btn ${block.content.textAlign === 'left' ? 'active' : ''}`}
                                onClick={() => updateBlockContent(block.id, { textAlign: 'left' })}
                            >
                                ⬅️
                            </button>
                            <button 
                                className={`align-btn ${block.content.textAlign === 'center' ? 'active' : ''}`}
                                onClick={() => updateBlockContent(block.id, { textAlign: 'center' })}
                            >
                                ↔️
                            </button>
                            <button 
                                className={`align-btn ${block.content.textAlign === 'right' ? 'active' : ''}`}
                                onClick={() => updateBlockContent(block.id, { textAlign: 'right' })}
                            >
                                ➡️
                            </button>
                        </div>

                        <label>Background Color</label>
                        <input
                            type="color"
                            value={block.style.backgroundColor}
                            onChange={(e) => updateBlock(block.id, { 
                                style: { ...block.style, backgroundColor: e.target.value }
                            })}
                        />
                    </div>
                );

            case 'button':
                return (
                    <div className="block-editor">
                        <label>Button Text</label>
                        <input
                            type="text"
                            value={block.content.text}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, text: e.target.value }
                            })}
                        />

                        <label>Button URL</label>
                        <input
                            type="url"
                            value={block.content.url}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, url: e.target.value }
                            })}
                        />

                        <label>Button Color</label>
                        <input
                            type="color"
                            value={block.content.backgroundColor}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, backgroundColor: e.target.value }
                            })}
                        />

                        <label>Text Color</label>
                        <input
                            type="color"
                            value={block.content.textColor}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, textColor: e.target.value }
                            })}
                        />

                        <label>Button Font</label>
                        <select
                            value={block.content.fontFamily}
                            onChange={(e) => updateBlockContent(block.id, { fontFamily: e.target.value })}
                        >
                            {emailFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                    {font.name} ({font.category})
                                </option>
                            ))}
                        </select>

                        <label>Button Alignment</label>
                        <div className="alignment-toggle">
                            <button 
                                className={`align-btn ${block.alignment === 'left' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'left' })}
                            >
                                ⬅️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'center' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'center' })}
                            >
                                ↔️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'right' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'right' })}
                            >
                                ➡️
                            </button>
                        </div>

                        <label>Background Color</label>
                        <input
                            type="color"
                            value={block.style.backgroundColor}
                            onChange={(e) => updateBlockStyle(block.id, { backgroundColor: e.target.value })}
                        />
                    </div>
                );

            case 'image':
                return (
                    <div className="block-editor">
                        <label>Upload Image</label>
                        <div className="image-upload-section">
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        uploadImage(file, block.id);
                                    }
                                }}
                                style={{ display: 'none' }}
                            />
                            <button 
                                className="upload-btn"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                {uploading ? '📤 Uploading...' : '📤 Upload Image'}
                            </button>
                        </div>

                        <label>Or Image URL</label>
                        <input
                            type="url"
                            value={block.content.src}
                            onChange={(e) => updateBlockContent(block.id, { src: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                        />

                        <label>Alt Text</label>
                        <input
                            type="text"
                            value={block.content.alt}
                            onChange={(e) => updateBlockContent(block.id, { alt: e.target.value })}
                            placeholder="Image description"
                        />

                        <label>Width</label>
                        <select
                            value={block.content.width}
                            onChange={(e) => updateBlockContent(block.id, { width: e.target.value })}
                        >
                            <option value="100%">Full Width</option>
                            <option value="300px">Small (300px)</option>
                            <option value="400px">Medium (400px)</option>
                            <option value="500px">Large (500px)</option>
                            <option value="600px">Extra Large (600px)</option>
                        </select>

                        <label>Image Alignment</label>
                        <div className="alignment-toggle">
                            <button 
                                className={`align-btn ${block.alignment === 'left' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'left' })}
                            >
                                ⬅️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'center' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'center' })}
                            >
                                ↔️
                            </button>
                            <button 
                                className={`align-btn ${block.alignment === 'right' ? 'active' : ''}`}
                                onClick={() => updateBlock(block.id, { alignment: 'right' })}
                            >
                                ➡️
                            </button>
                        </div>

                        <label>Background Color</label>
                        <input
                            type="color"
                            value={block.style.backgroundColor}
                            onChange={(e) => updateBlockStyle(block.id, { backgroundColor: e.target.value })}
                        />
                    </div>
                );

            case 'footer':
                return (
                    <div className="block-editor">
                        <label>Company Name</label>
                        <input
                            type="text"
                            value={block.content.companyName}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, companyName: e.target.value }
                            })}
                        />

                        <label>Address</label>
                        <textarea
                            value={block.content.address}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, address: e.target.value }
                            })}
                            rows={2}
                        />

                        <label>Text Color</label>
                        <input
                            type="color"
                            value={block.content.textColor}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, textColor: e.target.value }
                            })}
                        />

                        <label>Footer Font</label>
                        <select
                            value={block.content.fontFamily}
                            onChange={(e) => updateBlock(block.id, { 
                                content: { ...block.content, fontFamily: e.target.value }
                            })}
                        >
                            {emailFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                    {font.name} ({font.category})
                                </option>
                            ))}
                        </select>

                        <label>Background Color</label>
                        <input
                            type="color"
                            value={block.style.backgroundColor}
                            onChange={(e) => updateBlock(block.id, { 
                                style: { ...block.style, backgroundColor: e.target.value }
                            })}
                        />
                    </div>
                );

            default:
                return <div>No settings available</div>;
        }
    }
};

export default ProfessionalEmailBuilder;
